// Serviço do domínio documents/exams — arquivos no Storage privado + metadados.
import { createClient } from '@/lib/supabase/client'
import { registrarAuditoria } from '@/services/identity'
import { registrarEvento } from '@/services/timeline'

export type TipoDocumento = 'exame' | 'laudo' | 'imagem' | 'documento' | 'relatorio' | 'outro'
export type StatusDocumento =
  | 'enviado' | 'recebido' | 'aguardando_analise' | 'em_analise'
  | 'analisado' | 'invalido' | 'duplicado' | 'substituido'

export interface Documento {
  id: string
  workspace_id: string
  paciente_id: string
  consulta_id: string | null
  tipo: TipoDocumento
  categoria: string | null
  nome: string
  storage_path: string
  content_type: string | null
  tamanho_bytes: number | null
  origem: 'paciente' | 'equipe' | 'diagnostico360'
  status: StatusDocumento
  observacoes: string | null
  created_at: string
}

export const TIPO_DOC_LABEL: Record<TipoDocumento, string> = {
  exame: 'Exame',
  laudo: 'Laudo',
  imagem: 'Imagem',
  documento: 'Documento',
  relatorio: 'Relatório',
  outro: 'Outro',
}

export const STATUS_DOC_LABEL: Record<StatusDocumento, string> = {
  enviado: 'Enviado',
  recebido: 'Recebido',
  aguardando_analise: 'Aguardando análise',
  em_analise: 'Em análise',
  analisado: 'Analisado',
  invalido: 'Inválido',
  duplicado: 'Duplicado',
  substituido: 'Substituído',
}

const MAX_BYTES = 25 * 1024 * 1024 // 25 MB

export async function listDocumentos(workspaceId: string, pacienteId: string): Promise<Documento[]> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('documentos')
    .select('*')
    .eq('workspace_id', workspaceId)
    .eq('paciente_id', pacienteId)
    .order('created_at', { ascending: false })
    .limit(200)
  if (error) throw error
  return (data ?? []) as Documento[]
}

export async function listDocumentosParaAnalisar(workspaceId: string): Promise<(Documento & { paciente_nome: string })[]> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('documentos')
    .select('*, pacientes(nome)')
    .eq('workspace_id', workspaceId)
    .in('status', ['recebido', 'aguardando_analise', 'em_analise'])
    .order('created_at', { ascending: true })
    .limit(50)
  if (error) throw error
  return (data ?? []).map(row => {
    const raw = (row as Record<string, unknown>).pacientes
    const pac = (Array.isArray(raw) ? raw[0] : raw) as { nome: string } | null
    const { pacientes: _drop, ...resto } = row as Record<string, unknown>
    void _drop
    return { ...(resto as unknown as Documento), paciente_nome: pac?.nome ?? '—' }
  })
}

/** Upload pela equipe: valida tamanho/tipo, envia ao Storage e registra o documento. */
export async function uploadDocumento(params: {
  workspaceId: string
  pacienteId: string
  consultaId?: string | null
  tipo: TipoDocumento
  file: File
}): Promise<string> {
  if (params.file.size > MAX_BYTES) throw new Error('Arquivo acima de 25 MB.')
  const supabase = createClient()
  const seguro = params.file.name.replace(/[^\w.-]+/g, '_').slice(-80)
  const path = `${params.workspaceId}/${params.pacienteId}/${crypto.randomUUID()}-${seguro}`

  const { error: eUp } = await supabase.storage.from('documentos').upload(path, params.file, {
    contentType: params.file.type || 'application/octet-stream',
    upsert: false,
  })
  if (eUp) throw eUp

  const { data, error } = await supabase
    .from('documentos')
    .insert({
      workspace_id: params.workspaceId,
      paciente_id: params.pacienteId,
      consulta_id: params.consultaId ?? null,
      tipo: params.tipo,
      nome: params.file.name.slice(0, 200),
      storage_path: path,
      content_type: params.file.type || null,
      tamanho_bytes: params.file.size,
      origem: 'equipe',
      status: 'recebido',
    })
    .select('id')
    .single()
  if (error) throw error
  const id = (data as { id: string }).id

  await registrarEvento({
    workspaceId: params.workspaceId,
    pacienteId: params.pacienteId,
    consultaId: params.consultaId ?? null,
    tipo: 'documento_recebido',
    titulo: `Documento adicionado: ${params.file.name.slice(0, 120)}`,
    eventoDominio: 'document.received',
  })
  await registrarAuditoria({
    workspaceId: params.workspaceId,
    acao: 'documento_upload',
    entidade: 'documentos',
    registroId: id,
  })
  return id
}

/** URL assinada temporária (5 min) para ver/baixar — auditada. */
export async function urlAssinada(workspaceId: string, doc: Documento): Promise<string> {
  const supabase = createClient()
  const { data, error } = await supabase.storage.from('documentos').createSignedUrl(doc.storage_path, 300)
  if (error) throw error
  await registrarAuditoria({
    workspaceId,
    acao: 'documento_visualizado',
    entidade: 'documentos',
    registroId: doc.id,
  })
  return data.signedUrl
}

export async function mudarStatusDocumento(params: {
  workspaceId: string
  doc: Documento
  status: StatusDocumento
  observacoes?: string
}): Promise<void> {
  const supabase = createClient()
  const patch: Record<string, unknown> = { status: params.status }
  if (params.observacoes !== undefined) patch.observacoes = params.observacoes
  if (params.status === 'analisado') patch.analisado_em = new Date().toISOString()
  const { error } = await supabase
    .from('documentos')
    .update(patch)
    .eq('id', params.doc.id)
    .eq('workspace_id', params.workspaceId)
  if (error) throw error
  await registrarEvento({
    workspaceId: params.workspaceId,
    pacienteId: params.doc.paciente_id,
    consultaId: params.doc.consulta_id,
    tipo: 'documento_status',
    titulo: `Documento ${STATUS_DOC_LABEL[params.status].toLowerCase()}: ${params.doc.nome}`,
    visibilidade: params.status === 'analisado' ? 'medicos' : 'equipe',
  })
  await registrarAuditoria({
    workspaceId: params.workspaceId,
    acao: `documento_${params.status}`,
    entidade: 'documentos',
    registroId: params.doc.id,
  })
}
